import {getLayoutData, getPageData, getPages} from "@/pages/api/api";
import styled from 'styled-components';
import Container from "@/components/common/Container";
import {
	SEE_ALSO_INFO_BLOCK_QUERY,
	SINGLE_PAGE_QUERY,
	WHAT_WE_DO_CATEGORY_QUERY,
	WHAT_WE_DO_SINGLE_ACTION_QUERY
} from "@/pages/api/fragments";
import useSetBannerData from "@/hooks/useSetBannerData";
import ExtendedContent from "@/components/common/ExtendedContent";
import {CONTENT_TYPES, PAGES, SINGLE_PAGE_TYPES} from "@/constants";
import SeeAlso, {getSeeAlsoData} from "@/components/SeeAlso";
import {useMemo} from "react";

const StyledTypographyContainer = styled.div`
	padding: 50px 0;
	h1 {
		margin: 50px 0;
	}

	h1,h3, 
	p {
		margin-bottom: 30px;
		color: white;
	}
`;

const StyledContainerTester = styled.div`
	width: 100%;
	// background-color: ${({theme}) => theme.colors.darkDarkDarkRed};
	height: 70px;
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	color: white;
	&.red-square {
		height: 400px;
		background-color: ${({theme}) => theme.colors.chilliRed};
		padding: 50px;
		font-size:25px;
	}	
	&.bottom-square {
		margin-top:400px;
		height: 200px;
		background-color: ${({theme}) => theme.colors.gunmetal};
	}	
	&.top-square {
		margin-top:200px;
		height: 200px;
		background-color: ${({theme}) => theme.colors.gunmetal};
	}
	&.border {
		border: 1px solid white;
	}
	&.height {
		height: 150px;
	}
	&.black {
		background-color: ${({theme}) => theme.colors.black};
	}
	&.white {
		background-color: ${({theme}) => theme.colors.white};
		color: ${({theme}) => theme.colors.black};
	}
	&.grey {
		background-color: ${({theme}) => theme.colors.platinum};
	}
	&.darkBlue {
		background-color: ${({theme}) => theme.colors.richBlack};
	}
	&.darkDarkBlue {
		background-color: ${({theme}) => theme.colors.richBlack};
	}
	&.blue {
		background-color: ${({theme}) => theme.colors.gunmetal};
	}
	&.red {
		background-color: ${({theme}) => theme.colors.chilliRed};
	}
	&.darkRed {
		background-color: ${({theme}) => theme.colors.pennRed};
	}
	&.darkDarkRed {
		background-color: ${({theme}) => theme.colors.barnRed};
	}
	&.darkDarkDarkRed {
		// background-color: ${({theme}) => theme.colors.darkDarkDarkRed};
	}	
	&.lavenda {
		background-color: ${({theme}) => theme.colors.periwinkle};
	}	
	&.darkLavenda {
		background-color: ${({theme}) => theme.colors.chineseViolet};
	}	
	&.darkGrey {
		background-color: ${({theme}) => theme.colors.slateGray};
	}
`

const StyledPageContainer = styled(Container)`
	padding: 100px 0;
	h1, a {
		color: ${({theme}) => theme.colors.white};
		span {
			color: ${({theme}) => theme.colors.pennRed};
		}
	}
`

const EmptyContent = styled.div`
	width: 100%;
	height: 100vh;
`


export default function Page({ data }) {
	const bannerData = useSetBannerData(data)
	const content = data?.content || []
	const seeAlsoInfo = data?.seeAlsoInfo || { header: '', variant: ''}
	const formattedSeeAlsoData = useMemo(() => getSeeAlsoData(data),[data])

	return (
		<>
			<ExtendedContent content={content} globalBannerData={bannerData} />
			<SeeAlso
				header={seeAlsoInfo.header}
				variant={seeAlsoInfo.variant}
				data={formattedSeeAlsoData}
			/>
		</>
	)
}

const pathsQuery = `
	query GetSinglePages($locale: SiteLocale) {
		allSinglePages(locale: $locale, filter: {slug: {notIn: ["${PAGES.HOME}", "${PAGES.OUR_EVENTS}", "${PAGES.I_PAY}", "${PAGES.CONTACT}", "${PAGES.POLISH_RED_CROSS}"]}, pageType: {eq: "${SINGLE_PAGE_TYPES.POLISH_RED_CROSS}"}}) {
			pageType
            singlePageName
            slug
		}
	}
`;

const propsQuery = `
	query GetSinglePagesData($slug: String, $locale: SiteLocale)  {
		allSinglePages(locale: $locale, filter: {slug: {eq: $slug}}) {
			__typename
            ${SINGLE_PAGE_QUERY}
			seeAlsoInfo{
				${SEE_ALSO_INFO_BLOCK_QUERY}
			}
			seeAlsoLinks {
				__typename
				... on ${CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION} {
					${WHAT_WE_DO_SINGLE_ACTION_QUERY}
				}
				... on ${CONTENT_TYPES.ALL_NEWS_PAGE} {
					title
					slug
					card {
						header
						description
					}
				}
				... on ${CONTENT_TYPES.WHAT_WE_DO_CATEGORY} {
					${WHAT_WE_DO_CATEGORY_QUERY}
				}
				... on ${CONTENT_TYPES.SINGLE_PAGE} {
					__typename
					id
					pageType
					slug
					card {
						header
						description
					}
				}
		    }
        }
    }
`

export async function getStaticProps({ params, locale }) {
	const { slug } = params;
	const data = await getPageData('allSinglePages', slug, propsQuery, locale);
	const layout = await getLayoutData(locale)
	return {
		props: {
			data,
			locale,
			...layout,
			isDarkSearch: false
		},
		revalidate: 60 * 2
	};
}

export async function getStaticPaths({ locales }) {
	const paths = [];
	for (const locale of locales) {
		const singlePages = await getPages('allSinglePages', pathsQuery, locale,'prc');
		const single = singlePages
			.map((page) => {
				return {
					params: {
						slug: page.slug,
					},
					locale,
				};
			})
			.filter((el) => {
				return el !== null;
			});
		paths.push(...single);
	}
	return {
		paths,
		fallback: false,
	};
}
